/**
 * A space's reports (docs/REPORTS.md §4): every one the viewer may see, from the channel and from items'
 * conversations, newest first, filtered by label, poster, where it was posted and the time it covers (in the page's
 * address). Each row links to its place in its conversation.
 */

import { useState } from "preact/hooks";
import { api } from "../api/client.ts";
import type { CommentOut, ReportList, SpaceOut } from "../api/types.gen.ts";
import { type Key, t } from "../i18n/index.ts";
import { formatInstant, formatRelative } from "../lib/format.ts";
import { spaceApi } from "../lib/paths.ts";
import { periodText, placeHref, reportTitle } from "../lib/reports.ts";
import { type Resource, useResource } from "../lib/resource.ts";
import { location, setQuery } from "../lib/router.ts";
import type { PageProps } from "../lib/state.ts";
import { Person } from "../ui/Avatar.tsx";
import { Button, InlineButton, Link } from "../ui/Button.tsx";
import { Field, Input, Select } from "../ui/form.tsx";
import { Async, Badge, EmptyState } from "../ui/misc.tsx";
import { PageHeader } from "../ui/PageHeader.tsx";
import { LINK } from "../ui/styles.ts";

/** The filters the page's address can hold, as the API takes them. */
const FILTERS = ["label", "author", "item", "channel", "from", "to"] as const;

/** The page's filters, from its address. */
function filtersOf(sp: URLSearchParams): Record<string, string | null> {
  return Object.fromEntries(FILTERS.map((k) => [k, sp.get(k)]));
}

/** A page of reports, those before `before` (none: the newest). */
function useReports(space: SpaceOut, q: Record<string, string | null>, before?: string): Resource<ReportList> {
  return useResource(`reports:${space.id}:${JSON.stringify(q)}:${before ?? ""}`, (signal) =>
    api.get<ReportList>(spaceApi(space, "/reports"), { ...q, before: before ?? null }, signal),
  );
}

/** One report: its title (to its place), label, period, who posted it where, and when. */
function Row({ space, c }: { space: SpaceOut; c: CommentOut }) {
  const where = c.item || c.rule || c.entity || c.commit ? (c.subject_title ?? t("common.untitled")) : null;
  return (
    <li class="flex flex-col gap-1 px-4 py-3 text-sm" data-report-row={c.id}>
      <span class="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
        <Link href={placeHref(space, c)} class={`${LINK} min-w-0 truncate font-medium`}>
          {reportTitle(c)}
        </Link>
        {c.label ? <Badge tone="blue">{c.label}</Badge> : null}
        {c.period ? <span class="text-xs text-zinc-600 tabular-nums">{periodText(c.period)}</span> : null}
      </span>
      <span class="flex min-w-0 flex-wrap items-center gap-x-2 text-xs text-zinc-500">
        <Person id={c.author} name={c.author_name} kind={c.author_kind} />
        <span class="truncate">{where ? t("reports.inItem", { title: where }) : t("reports.inChannel")}</span>
        <span title={formatInstant(c.created_at)}>{formatRelative(c.created_at)}</span>
      </span>
    </li>
  );
}

/** A page of the list, and the way to the next. */
function Page({
  space,
  r,
  q,
  first,
}: {
  space: SpaceOut;
  r: Resource<ReportList>;
  q: Record<string, string | null>;
  first: boolean;
}) {
  return (
    <Async r={r}>
      {(page) =>
        page.data.length === 0 && first ? (
          <EmptyState title={t("reports.empty")}>{t("reports.emptyHint")}</EmptyState>
        ) : (
          <>
            <ul class="divide-y divide-zinc-100 rounded-lg border border-zinc-200 bg-white">
              {page.data.map((c) => (
                <Row key={c.id} space={space} c={c} />
              ))}
            </ul>
            {page.next ? <More space={space} q={q} before={page.next} /> : null}
          </>
        )
      }
    </Async>
  );
}

/** Older reports, once asked for. */
function More({ space, q, before }: { space: SpaceOut; q: Record<string, string | null>; before: string }) {
  const [shown, setShown] = useState(false);
  if (!shown) {
    return (
      <div class="flex justify-center">
        <Button onClick={() => setShown(true)}>{t("reports.more")}</Button>
      </div>
    );
  }
  return <OlderPage space={space} q={q} before={before} />;
}

function OlderPage({ space, q, before }: { space: SpaceOut; q: Record<string, string | null>; before: string }) {
  return <Page space={space} r={useReports(space, q, before)} q={q} first={false} />;
}

/** Where reports were posted, as the filter offers it: anywhere, the channel, items' conversations (and the rest). */
const PLACES: [string, Key][] = [
  ["", "reports.filter.anywhere"],
  ["true", "reports.filter.channel"],
  ["false", "reports.filter.elsewhere"],
];

/** The filters: label, poster, place, the time covered. */
function Filters({ list, q }: { list: ReportList | undefined; q: Record<string, string | null> }) {
  const labels = list?.labels ?? [];
  const posters = list?.posters ?? [];
  return (
    <div class="flex flex-wrap items-end gap-3" data-report-filters="">
      <Field label={t("reports.filter.label")}>
        <Select
          compact={true}
          value={q["label"] ?? ""}
          options={[{ value: "", label: t("reports.filter.anyLabel") }, ...labels.map((l) => ({ value: l, label: l }))]}
          onValue={(v) => setQuery({ label: v })}
        />
      </Field>
      <Field label={t("reports.filter.poster")}>
        <Select
          compact={true}
          value={q["author"] ?? ""}
          options={[
            { value: "", label: t("reports.filter.anyone") },
            ...posters.map((p) => ({ value: p.id, label: p.name })),
          ]}
          onValue={(v) => setQuery({ author: v })}
        />
      </Field>
      <Field label={t("reports.filter.place")}>
        <Select
          compact={true}
          value={q["channel"] ?? ""}
          options={PLACES.map(([value, label]) => ({ value, label: t(label) }))}
          onValue={(v) => setQuery({ channel: v, item: null })}
        />
      </Field>
      <Field label={t("reports.filter.from")}>
        <Input compact={true} type="date" value={q["from"] ?? ""} onValue={(v) => setQuery({ from: v })} />
      </Field>
      <Field label={t("reports.filter.to")}>
        <Input compact={true} type="date" value={q["to"] ?? ""} onValue={(v) => setQuery({ to: v })} />
      </Field>
      {FILTERS.some((k) => q[k]) ? (
        <InlineButton size="xs" onClick={() => setQuery(Object.fromEntries(FILTERS.map((k) => [k, null])))}>
          {t("reports.filter.clear")}
        </InlineButton>
      ) : null}
    </div>
  );
}

/** The space's reports, newest first, with their filters. */
export function Reports({ space }: PageProps) {
  const q = filtersOf(location.value.searchParams);
  const r = useReports(space, q);
  const item = q["item"];
  return (
    <>
      <PageHeader title={t("reports.title")} />
      <div class="flex max-w-4xl flex-col gap-4">
        <Filters list={r.data} q={q} />
        {item ? (
          <p class="flex items-center gap-2 text-sm text-zinc-600">
            {t("reports.inItem", { title: r.data?.data[0]?.subject_title ?? t("common.untitled") })}
            <InlineButton size="xs" onClick={() => setQuery({ item: null })}>
              {t("reports.filter.anywhere")}
            </InlineButton>
          </p>
        ) : null}
        <Page key={JSON.stringify(q)} space={space} r={r} q={q} first={true} />
      </div>
    </>
  );
}
